import React, { useState, useEffect, useCallback } from 'react';
import { PackageX, Download, Loader2, Layers, Hourglass, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { fmtDate } from '@/lib/itPrintSheets';
import { getFobPackedNotTransferred, downloadCsv } from '@/services/sectionReportsApi';
import { ReportHeader, StatCard, DateSearchFilters, Pager } from '@/components/sectionReports/ReportParts';

const PAGE_SIZE = 50;
const EMPTY = { dateFrom: '', dateTo: '', search: '', minDays: '0' };
const toParams = (f) => ({
  date_from: f.dateFrom || undefined, date_to: f.dateTo || undefined,
  search: f.search.trim() || undefined, min_days: Number(f.minDays) || undefined,
});
const ageClass = (d) => (d >= 7 ? 'bg-red-100 text-red-700' : d >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700');

const MinDaysSelect = ({ value, onChange }) => (
  <div>
    <label className="text-sm font-medium text-gray-700">Waiting at least</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="filter-min-days"
      className="w-full h-10 border border-input rounded-md px-3 text-sm bg-white">
      {[['0', 'Any age'], ['1', '1+ days'], ['3', '3+ days'], ['7', '7+ days'], ['15', '15+ days'], ['30', '30+ days']].map(([v, l]) => (
        <option key={v} value={v}>{l}</option>
      ))}
    </select>
  </div>
);

const PackedNotTransferredReport = () => {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState({ rows: [], total: 0, total_pages: 1, page: 1, summary: {} });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (page = 1, f = filters) => {
    setLoading(true);
    try {
      setData(await getFobPackedNotTransferred({ page, page_size: PAGE_SIZE, ...toParams(f) }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadCsv('/section-reports/fob/packed-not-transferred/export', toParams(filters),
        `fob_packed_not_transferred_${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const rows = data.rows || [];
  const s = data.summary || {};
  const start = ((data.page || 1) - 1) * PAGE_SIZE;
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="fob-packed-not-transferred-report">
      <ReportHeader icon={PackageX} title="Packed – Not Transferred" subtitle="FOB · Assortment Packing done, Internal Transfer pending"
        backRoute="/sections/fob"
        actions={(
          <Button onClick={exportCsv} disabled={exporting || loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pnt-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Export CSV
          </Button>
        )} />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="Bundles Waiting" value={(s.total_bundles || 0).toLocaleString()} icon={Layers} testId="pnt-stat-bundles" />
          <StatCard label="Pieces Waiting" value={(s.total_qty || 0).toLocaleString()} icon={Boxes} testId="pnt-stat-qty" tone="text-indigo-500" />
          <StatCard label="Oldest (days)" value={s.oldest_days || 0} icon={Hourglass} testId="pnt-stat-oldest" tone="text-red-500" />
        </div>
        <DateSearchFilters filters={filters} loading={loading} placeholder="Bundle, document, sort no, staff…"
          onChange={(p) => setFilters((f) => ({ ...f, ...p }))} onSearch={() => load(1)}
          onClear={() => { setFilters(EMPTY); load(1, EMPTY); }}>
          <MinDaysSelect value={filters.minDays} onChange={(v) => setFilters((f) => ({ ...f, minDays: v }))} />
        </DateSearchFilters>
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="pnt-table">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    {['#', 'Bundle No', 'Document', 'Sort No', 'Qty', 'Packed At', 'Staff', 'Days Waiting'].map((h) => (
                      <TableHead key={h} className={`font-semibold ${['Qty', 'Days Waiting'].includes(h) ? 'text-right' : ''}`}>{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-10 text-gray-500" data-testid="pnt-empty">Every packed bundle has been transferred.</TableCell></TableRow>
                  ) : rows.map((r, i) => (
                    <TableRow key={r.bundle_barcode} data-testid={`pnt-row-${r.bundle_barcode}`}>
                      <TableCell className="text-gray-400 text-sm">{start + i + 1}</TableCell>
                      <TableCell className="font-mono font-semibold">{r.bundle_barcode}</TableCell>
                      <TableCell className="font-mono text-sm">{r.document_barcode || '—'}</TableCell>
                      <TableCell>{r.sort_no || '—'}</TableCell>
                      <TableCell className="text-right font-semibold text-[#2D5F4F]">{r.qty}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{r.packed_at ? fmtDate(r.packed_at) : '—'}</TableCell>
                      <TableCell className="text-sm max-w-[220px] truncate" title={r.staff}>{r.staff || '—'}</TableCell>
                      <TableCell className="text-right">
                        <Badge className={`${ageClass(r.days_waiting)} hover:opacity-100`} data-testid={`pnt-days-${r.bundle_barcode}`}>{r.days_waiting}d</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pager page={data.page || 1} pageSize={PAGE_SIZE} total={data.total || 0} totalPages={data.total_pages || 1}
              loading={loading} onPage={(p) => load(p)} noun="bundles" />
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PackedNotTransferredReport;
